import cn from "@/utils/class-names";
import dayjs from "dayjs";
import React from "react";
import { Text } from "rizzui";
import {
  NotificationItem as NotificationItemType,
  useNotification,
} from "./NotificationContext";
import NotificationRepository from "@/utilities/repositories/Notification";
import { IoMdClose } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";

interface NotificationItemProps {
  notification: NotificationItemType;
  onClose?: () => void;
  handleDelete?: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string,
  ) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  handleDelete,
}) => {
  const navigate = useNavigate();
  const { markAsRead, minusOneReadCountApi } = useNotification();

  const { id, title, message, timestamp, isRead, type, userId, data } =
    notification;

  // const getTypeColor = () => {
  //   switch (type) {
  //     case "success":
  //       return "text-green-600 dark:text-green-400";
  //     case "warning":
  //       return "text-amber-600 dark:text-amber-400";
  //     case "error":
  //       return "text-red-600 dark:text-red-400";
  //     default:
  //       return "text-blue-600 dark:text-blue-400";
  //   }
  // };

  const onRead = async (id: string) => {
    try {
      await NotificationRepository.readNotification(id);
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAsRead = async () => {
    if (isRead) return;

    try {
      // Update local state first for better UX
      markAsRead(id);
      minusOneReadCountApi();
      // Then call API
      await onRead(id);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleClick = async () => {
    try {
      // Handle notification click with appropriate filters
      if (userId && type === "NO_LEAD") {
        // Navigate to enquiry entries with userId filter
        const filterUrl = `${routes.contacts.listing}?search=&page=1&limit=10&userId=${userId}`;
        navigate(filterUrl, { replace: true });
      } else if (type === "NEW_LEAD") {
        // Navigate to enquiry entries, with leadId filter if available
        const leadId = data?.leadId;
        const filterUrl = leadId
          ? `${routes.contacts.listing}?leadId=${encodeURIComponent(leadId)}`
          : routes.contacts.listing;
        navigate(filterUrl, { replace: true });
      } else if (type === "NO_LEAD") {
        navigate(routes.contacts.listing, { replace: true });
      } else {
        navigate(routes.package.payment, { replace: true });
      }
      // Then mark as read
      handleMarkAsRead();
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        "group flex flex-row gap-3 rounded-md p-3 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 w-full cursor-pointer max-w-[256px] sm:max-w-[336px] md:max-w-full",
        isRead ? "bg-white dark:bg-gray-900" : "bg-gray-200 dark:bg-gray-700",
      )}
    >
      <div className="flex-1 w-full">
        <div className="flex flex-row justify-between items-start">
          <Text className="text-start mb-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100 overflow-ellipsis">
            {title}
          </Text>
          <button
            type="button"
            onClick={(e) => handleDelete?.(e, id)}
            aria-label="Delete notification"
          >
            <IoMdClose size={18} />
          </button>
        </div>

        <div
          className="text-start mb-1 text-sm text-gray-600 dark:text-gray-400 notification-html-content"
          dangerouslySetInnerHTML={{
            __html: message || "",
          }}
        ></div>
        <Text className="text-start text-xs text-gray-500 dark:text-gray-500">
          {dayjs(timestamp).fromNow()}
        </Text>
      </div>
      {/* <div className="flex flex-shrink-0 flex-auto items-center gap-1">
          {!isRead && (
            <ActionIcon
              size="sm"
              variant="text"
              onClick={handleMarkAsRead}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <PiCheck className="h-4 w-4" />
            </ActionIcon>
          )}
          <ActionIcon
            size="sm"
            variant="text"
            onClick={handleRemove}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <PiX className="h-4 w-4" />
          </ActionIcon>
        </div> */}
    </div>
  );
};

export default NotificationItem;
